import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Tab,
  Tabs,
  AppBar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  InputAdornment,
  Card,
  CardContent,
  Collapse,
  ListItemIcon,
  ListItemButton,
  Drawer,
  Toolbar,
  Menu,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Logout as LogoutIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Map as MapIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  Route as RouteIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  PhotoLibrary as GalleryIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import useResponsive from '../hooks/useResponsive';
import axios from 'axios';
import DetailView from './DetailView';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import FieldOfficerDialog from './FieldOfficerDialog';
import { BACKEND_BASE_URL } from '../config';
import CorridorDashboard from './CorridorDashboard';
import * as XLSX from 'xlsx';

// Indian State Codes
const INDIAN_STATES = [
  { code: 'AN', name: 'Andaman and Nicobar Islands' },
  { code: 'AP', name: 'Andhra Pradesh' },
  { code: 'AR', name: 'Arunachal Pradesh' },
  { code: 'AS', name: 'Assam' },
  { code: 'BR', name: 'Bihar' },
  { code: 'CH', name: 'Chandigarh' },
  { code: 'CT', name: 'Chhattisgarh' },
  { code: 'DL', name: 'Delhi' },
  { code: 'GA', name: 'Goa' },
  { code: 'GJ', name: 'Gujarat' },
  { code: 'HR', name: 'Haryana' },
  { code: 'HP', name: 'Himachal Pradesh' },
  { code: 'JK', name: 'Jammu and Kashmir' },
  { code: 'JH', name: 'Jharkhand' },
  { code: 'KA', name: 'Karnataka' },
  { code: 'KL', name: 'Kerala' },
  { code: 'MP', name: 'Madhya Pradesh' },
  { code: 'MH', name: 'Maharashtra' },
  { code: 'MN', name: 'Manipur' },
  { code: 'ML', name: 'Meghalaya' },
  { code: 'MZ', name: 'Mizoram' },
  { code: 'NL', name: 'Nagaland' },
  { code: 'OR', name: 'Odisha' },
  { code: 'PY', name: 'Puducherry' },
  { code: 'PB', name: 'Punjab' },
  { code: 'RJ', name: 'Rajasthan' },
  { code: 'SK', name: 'Sikkim' },
  { code: 'TN', name: 'Tamil Nadu' },
  { code: 'TS', name: 'Telangana' },
  { code: 'TR', name: 'Tripura' },
  { code: 'UP', name: 'Uttar Pradesh' },
  { code: 'UT', name: 'Uttarakhand' },
  { code: 'WB', name: 'West Bengal' }
];

// Utility function to format dates in Indian format (DD/MM/YYYY)
const formatIndianDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function TabPanel({ children, value, index }) {
  return (
    <div
      hidden={value !== index}
      style={{
        padding: 0,
        height: value === index ? 'auto' : 0,
        overflow: value === index ? 'visible' : 'hidden'
      }}
    >
      {value === index && children}
    </div>
  );
}

function KPICard({ title, value, icon, height }) {
  const { isSmallMobile, isMobile } = useResponsive();

  // Responsive height calculation
  const getResponsiveHeight = () => {
    if (isSmallMobile) return height ? Math.min(parseInt(height), 80) + 'px' : '80px';
    if (isMobile) return height ? Math.min(parseInt(height), 100) + 'px' : '100px';
    return height || '120px';
  };

  // Responsive padding
  const getResponsivePadding = () => {
    if (isSmallMobile) return 1;
    if (isMobile) return 1.25;
    return 1.5;
  };

  // Responsive icon size
  const getIconSize = () => {
    if (isSmallMobile) return '1rem';
    if (isMobile) return '1.2rem';
    const numHeight = parseInt(height) || 120;
    return numHeight <= 100 ? '1.2rem' : numHeight <= 120 ? '1.5rem' : '2rem';
  };

  // Responsive title font size
  const getTitleFontSize = () => {
    if (isSmallMobile) return '0.7rem';
    if (isMobile) return '0.75rem';
    const numHeight = parseInt(height) || 120;
    return numHeight <= 100 ? '0.7rem' : numHeight <= 120 ? '0.75rem' : '0.8rem';
  };

  // Responsive value font size
  const getValueFontSize = () => {
    if (isSmallMobile) return '0.875rem';
    if (isMobile) return '1rem';
    const numHeight = parseInt(height) || 120;
    return numHeight <= 100 ? '0.9rem' : numHeight <= 120 ? '1rem' : '1.1rem';
  };

  return (
    <Card sx={{
      height: getResponsiveHeight(),
      display: 'flex',
      flexDirection: 'column',
      minHeight: isSmallMobile ? '60px' : '80px'
    }}>
      <CardContent sx={{
        p: getResponsivePadding(),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '&:last-child': { pb: getResponsivePadding() }
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: isSmallMobile ? 0.5 : 1,
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          {icon && (
            <Box sx={{
              mr: isSmallMobile ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              '& svg': {
                fontSize: getIconSize()
              }
            }}>
              {icon}
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: getTitleFontSize(),
              lineHeight: 1.2,
              wordBreak: 'break-word',
              hyphens: 'auto'
            }}
            className="responsive-text"
          >
            {title}
          </Typography>
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: '#F79233',
            fontSize: getValueFontSize(),
            fontWeight: 'bold',
            lineHeight: 1.1,
            wordBreak: 'break-word',
            textAlign: isSmallMobile ? 'left' : 'inherit'
          }}
          className="responsive-text"
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ProjectDashboard({ project }) {
  const today = new Date();
  const endDate = new Date(project.end_date);
  const remainingDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  const totalStretch = project.line_items.reduce((sum, item) => sum + parseFloat(item.stretch_km), 0);
  const { isSmallMobile, isMobile, getContainerPadding, getGridBreakpoints } = useResponsive();

  // Document Gallery state
  const [documentGallery, setDocumentGallery] = useState({
    open: false,
    stage: '',
    stageName: '',
    loading: false,
    documents: {},
    viewSize: 'medium'
  });

  const fetchStageDocuments = async (stage, stageName) => {
    setDocumentGallery(prev => ({ ...prev, open: true, stage, stageName, loading: true }));

    try {
      const corridorDocuments = {};

      // Fetch documents for each corridor in the selected project
      for (const corridor of project.line_items) {
        try {
          const encodedCorridorId = encodeURIComponent(corridor.corridor_id);
          const response = await axios.get(
            `${BACKEND_BASE_URL}/api/field-officer/work-status/${project._id}/${encodedCorridorId}`
          );

          if (response.data && response.data[stage] && response.data[stage].documents) {
            const stageDocuments = response.data[stage].documents;
            const hasDocuments = Object.values(stageDocuments).some(docs => docs && docs.length > 0);

            if (hasDocuments) {
              corridorDocuments[corridor.corridor_id] = {
                corridor_name: corridor.corridor,
                documents: stageDocuments
              };
            }
          }
        } catch (error) {
          console.error(`Error fetching documents for corridor ${corridor.corridor_id}:`, error);
        }
      }

      setDocumentGallery(prev => ({
        ...prev,
        documents: corridorDocuments,
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching stage documents:', error);
      setDocumentGallery(prev => ({ ...prev, loading: false }));
    }
  };

  const handleGalleryClose = () => {
    setDocumentGallery({
      open: false,
      stage: '',
      stageName: '',
      loading: false,
      documents: {},
      viewSize: 'medium'
    });
  };

  const handleViewSizeChange = (event, newSize) => {
    if (newSize !== null) {
      setDocumentGallery(prev => ({ ...prev, viewSize: newSize }));
    }
  };

  return (
    <Box sx={{ p: getContainerPadding() }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: isSmallMobile ? 2 : 4,
        flexDirection: isSmallMobile ? 'column' : 'row',
        textAlign: isSmallMobile ? 'center' : 'left'
      }}>
        <Typography
          variant={isSmallMobile ? "h5" : isMobile ? "h4" : "h4"}
          sx={{
            color: '#F79233',
            flexGrow: 1,
            fontSize: isSmallMobile ? '1.5rem' : isMobile ? '2rem' : '2.125rem',
            mb: isSmallMobile ? 1 : 0
          }}
          className="responsive-text"
        >
          Project Analysis: {project.po_number}
        </Typography>
        {project.logo && (
          <Box
            component="img"
            src={project.logo}
            alt="Project Logo"
            sx={{
              height: isSmallMobile ? '40px' : '60px',
              width: 'auto',
              objectFit: 'contain',
              ml: isSmallMobile ? 0 : 2
            }}
          />
        )}
      </Box>

      <Grid container spacing={isSmallMobile ? 1 : isMobile ? 2 : 3} sx={{ mb: isSmallMobile ? 2 : 4 }}>
        <Grid item {...getGridBreakpoints(12, 6, 2.4)}>
          <KPICard
            title="PO Date"
            value={formatIndianDate(project.po_date)}
            icon={<CalendarIcon sx={{ color: '#F79233' }} />}
            height={isSmallMobile ? "80px" : isMobile ? "100px" : "120px"}
          />
        </Grid>
        <Grid item {...getGridBreakpoints(12, 6, 2.4)}>
          <KPICard
            title="PO Party"
            value={project.po_party_name}
            icon={<BusinessIcon sx={{ color: '#F79233' }} />}
            height={isSmallMobile ? "80px" : isMobile ? "100px" : "120px"}
          />
        </Grid>
        <Grid item {...getGridBreakpoints(12, 6, 2.4)}>
          <KPICard
            title="Completion Days"
            value={`${project.completion_days} days`}
            icon={<TimelineIcon sx={{ color: '#F79233' }} />}
            height={isSmallMobile ? "80px" : isMobile ? "100px" : "120px"}
          />
        </Grid>
        <Grid item {...getGridBreakpoints(12, 6, 2.4)}>
          <KPICard
            title="Total Project Stretch"
            value={`${totalStretch.toFixed(2)} KM`}
            icon={<RouteIcon sx={{ color: '#F79233' }} />}
            height={isSmallMobile ? "80px" : isMobile ? "100px" : "120px"}
          />
        </Grid>
        <Grid item {...getGridBreakpoints(12, 6, 2.4)}>
           <KPICard
            title="Number of Corridors"
            value={project.line_items.length}
            icon={<MapIcon sx={{ color: '#F79233'}}/>}
            height={isSmallMobile ? "80px" : isMobile ? "100px" : "120px"}
           />
        </Grid>
      </Grid>

      <Grid container spacing={isSmallMobile ? 1 : isMobile ? 2 : 3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: isSmallMobile ? 2 : 3 }}>
            <CardContent sx={{ p: isSmallMobile ? 1.5 : 2 }}>
              <Typography
                variant={isSmallMobile ? "subtitle1" : "h6"}
                sx={{
                  mb: isSmallMobile ? 1 : 2,
                  color: '#F79233',
                  fontSize: isSmallMobile ? '1rem' : '1.25rem'
                }}
              >
                Project Overview
              </Typography>
              <Grid container spacing={isSmallMobile ? 1 : 2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant={isSmallMobile ? "body2" : "body1"}
                    className="responsive-text"
                  >
                    Total Corridors: {project.line_items.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography
                    variant={isSmallMobile ? "body2" : "body1"}
                    className="responsive-text"
                  >
                    End Date: {formatIndianDate(project.end_date)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ mb: isSmallMobile ? 2 : 3 }}>
            <CardContent sx={{ p: isSmallMobile ? 1.5 : 2 }}>
              <Typography
                variant={isSmallMobile ? "subtitle1" : "h6"}
                sx={{
                  mb: isSmallMobile ? 1 : 2,
                  color: '#F79233',
                  fontSize: isSmallMobile ? '1rem' : '1.25rem'
                }}
              >
                Stage Deadlines
              </Typography>
              <Grid container spacing={isSmallMobile ? 1 : 2}>
                {/* Site Visit - Show only if at least one subsection is enabled */}
                {project.document_requirements?.site_visit &&
                 Object.values(project.document_requirements.site_visit).some(Boolean) && (
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
                      gap: isSmallMobile ? 1 : 0
                    }}>
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        className="responsive-text"
                        sx={{ flex: 1 }}
                      >
                        <strong>Site Visit:</strong> {formatIndianDate(project.stage_deadlines.site_visit)}
                      </Typography>
                      <Tooltip title="View docs">
                        <IconButton
                          size="small"
                          onClick={() => fetchStageDocuments('site_visit', 'Site Visit')}
                          sx={{ color: '#F79233' }}
                        >
                          <GalleryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}

                {/* Design Approval - Show only if at least one subsection is enabled */}
                {project.document_requirements?.design_approval &&
                 Object.values(project.document_requirements.design_approval).some(Boolean) && (
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
                      gap: isSmallMobile ? 1 : 0
                    }}>
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        className="responsive-text"
                        sx={{ flex: 1 }}
                      >
                        <strong>Design Approval:</strong> {formatIndianDate(project.stage_deadlines.design_approval)}
                      </Typography>
                      <Tooltip title="View docs">
                        <IconButton
                          size="small"
                          onClick={() => fetchStageDocuments('design_approval', 'Design Approval')}
                          sx={{ color: '#F79233' }}
                        >
                          <GalleryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}

                {/* Material Delivery - Show only if at least one subsection is enabled */}
                {project.document_requirements?.product_delivery &&
                 Object.values(project.document_requirements.product_delivery).some(Boolean) && (
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
                      gap: isSmallMobile ? 1 : 0
                    }}>
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        className="responsive-text"
                        sx={{ flex: 1 }}
                      >
                        <strong>Material Delivery:</strong> {formatIndianDate(project.stage_deadlines.product_delivery)}
                      </Typography>
                      <Tooltip title="View docs">
                        <IconButton
                          size="small"
                          onClick={() => fetchStageDocuments('product_delivery', 'Material Delivery')}
                          sx={{ color: '#F79233' }}
                        >
                          <GalleryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}

                {/* Installation - Show only if at least one subsection is enabled */}
                {project.document_requirements?.installation &&
                 Object.values(project.document_requirements.installation).some(Boolean) && (
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
                      gap: isSmallMobile ? 1 : 0
                    }}>
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        className="responsive-text"
                        sx={{ flex: 1 }}
                      >
                        <strong>Installation:</strong> {formatIndianDate(project.stage_deadlines.installation)}
                      </Typography>
                      <Tooltip title="View docs">
                        <IconButton
                          size="small"
                          onClick={() => fetchStageDocuments('installation', 'Installation')}
                          sx={{ color: '#F79233' }}
                        >
                          <GalleryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Document Gallery Dialog */}
      <Dialog
        open={documentGallery.open}
        onClose={handleGalleryClose}
        {...(isMobile ? {
          fullScreen: isSmallMobile,
          maxWidth: 'md',
          fullWidth: true,
          PaperProps: {
            sx: {
              height: isSmallMobile ? '100vh' : '90vh',
              margin: isSmallMobile ? 0 : 2,
              maxHeight: isSmallMobile ? '100vh' : '90vh'
            }
          }
        } : {
          maxWidth: "lg",
          fullWidth: true,
          PaperProps: {
            sx: { height: '90vh' }
          }
        })}
      >
        <DialogTitle sx={{ p: isSmallMobile ? 1 : 2 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isSmallMobile ? 'flex-start' : 'center',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? 1 : 0
          }}>
            <Typography
              variant={isSmallMobile ? "subtitle1" : "h6"}
              sx={{
                fontSize: isSmallMobile ? '1rem' : '1.25rem',
                mb: isSmallMobile ? 1 : 0
              }}
              className="responsive-text"
            >
              {documentGallery.stageName} Documents
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: isSmallMobile ? 1 : 2,
              width: isSmallMobile ? '100%' : 'auto',
              justifyContent: isSmallMobile ? 'space-between' : 'flex-end'
            }}>
              <ToggleButtonGroup
                value={documentGallery.viewSize}
                exclusive
                onChange={handleViewSizeChange}
                size={isSmallMobile ? "small" : "small"}
                sx={{
                  '& .MuiToggleButton-root': {
                    padding: isSmallMobile ? '4px 8px' : '6px 12px'
                  }
                }}
              >
                <ToggleButton value="small">
                  <ZoomOutIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="medium">
                  <VisibilityIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="large">
                  <ZoomInIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton
                onClick={handleGalleryClose}
                size={isSmallMobile ? "small" : "medium"}
              >
                <CloseIcon fontSize={isSmallMobile ? "small" : "medium"} />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: isSmallMobile ? 1 : 2,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: isSmallMobile ? '4px' : '8px'
          }
        }}>
          {documentGallery.loading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              p: isSmallMobile ? 2 : 4
            }}>
              <CircularProgress sx={{ color: '#F79233' }} />
            </Box>
          ) : Object.keys(documentGallery.documents).length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              p: isSmallMobile ? 2 : 4
            }}>
              <Typography
                variant={isSmallMobile ? "body1" : "h6"}
                color="text.secondary"
                className="responsive-text"
              >
                No documents found for this stage
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              maxHeight: isSmallMobile ? 'calc(100vh - 120px)' : '70vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: isSmallMobile ? '4px' : '8px'
              }
            }}>
              {Object.entries(documentGallery.documents).map(([corridorId, corridorData]) => (
                <Box key={corridorId} sx={{ mb: isSmallMobile ? 2 : 4 }}>
                  <Typography
                    variant={isSmallMobile ? "subtitle1" : "h6"}
                    sx={{
                      mb: isSmallMobile ? 1 : 2,
                      color: '#F79233',
                      borderBottom: '1px solid #F79233',
                      pb: 1,
                      fontSize: isSmallMobile ? '1rem' : '1.25rem'
                    }}
                    className="responsive-text"
                  >
                    Corridor: {corridorData.corridor_name} ({corridorId})
                  </Typography>

                  {Object.entries(corridorData.documents).map(([docType, documents]) => {
                    if (!documents || documents.length === 0) return null;

                    const getDocTypeLabel = (type) => {
                      const labels = {
                        site_visit_report: 'Site Visit Report',
                        photos_with_geolocation: 'Photos with Geolocation',
                        approved_designs_pdf: 'Approved Designs PDF',
                        photos: 'Photos',
                        eway_bill: 'E-way Bill',
                        delivery_photos: 'Delivery Photos',
                        proforma_invoice: 'Proforma Invoice',
                        acknowledged_grn: 'Acknowledged GRN',
                        installation_photos: 'Installation Photos',
                        handover_report: 'Handover Report'
                      };
                      return labels[type] || type;
                    };

                    const getImageSize = () => {
                      switch (documentGallery.viewSize) {
                        case 'small': return { width: 80, height: 80 };
                        case 'medium': return { width: 120, height: 120 };
                        case 'large': return { width: 160, height: 160 };
                        default: return { width: 120, height: 120 };
                      }
                    };

                    const imageSize = getImageSize();

                    return (
                      <Box key={docType} sx={{ mb: isSmallMobile ? 2 : 3 }}>
                        <Typography
                          variant={isSmallMobile ? "body1" : "subtitle1"}
                          sx={{
                            mb: 1,
                            fontWeight: 'bold',
                            fontSize: isSmallMobile ? '0.875rem' : '1rem',
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                            overflowWrap: 'break-word'
                          }}
                          className="responsive-text"
                        >
                          {getDocTypeLabel(docType)}
                        </Typography>
                        <Grid container spacing={isSmallMobile ? 0.5 : 1}>
                          {documents.map((doc, index) => {
                            const isImage = typeof doc === 'object' && doc.file_path &&
                              (doc.file_path.toLowerCase().includes('.jpg') ||
                               doc.file_path.toLowerCase().includes('.jpeg') ||
                               doc.file_path.toLowerCase().includes('.png'));

                            const documentUrl = typeof doc === 'string'
                              ? `${BACKEND_BASE_URL}/uploads/${doc}`
                              : doc.presigned_url;

                            const filename = typeof doc === 'string' ? doc : doc.filename;

                            return (
                              <Grid item key={index}>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  {isImage ? (
                                    <Box
                                      component="img"
                                      src={documentUrl}
                                      alt={filename}
                                      sx={{
                                        width: imageSize.width,
                                        height: imageSize.height,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        border: '1px solid #ddd',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.8 }
                                      }}
                                      onClick={() => window.open(documentUrl, '_blank')}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: imageSize.width,
                                        height: imageSize.height,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'grey.100',
                                        borderRadius: 1,
                                        border: '1px solid #ddd',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'grey.200' }
                                      }}
                                      onClick={() => window.open(documentUrl, '_blank')}
                                    >
                                      <VisibilityIcon sx={{
                                        fontSize: isSmallMobile ? 24 : 32,
                                        color: '#F79233',
                                        mb: 1
                                      }} />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          textAlign: 'center',
                                          px: 1,
                                          fontSize: isSmallMobile ? '0.6rem' : '0.75rem'
                                        }}
                                        className="responsive-text"
                                      >
                                        {filename?.length > (isSmallMobile ? 10 : 15)
                                          ? `${filename.substring(0, isSmallMobile ? 10 : 15)}...`
                                          : filename}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Download button */}
                                  <IconButton
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: isSmallMobile ? 2 : 4,
                                      right: isSmallMobile ? 2 : 4,
                                      bgcolor: 'rgba(247, 146, 51, 0.8)',
                                      color: 'white',
                                      width: isSmallMobile ? 20 : 24,
                                      height: isSmallMobile ? 20 : 24,
                                      '&:hover': { bgcolor: 'rgba(247, 146, 51, 1)' }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(documentUrl, '_blank');
                                    }}
                                  >
                                    <DownloadIcon sx={{ fontSize: isSmallMobile ? 12 : 14 }} />
                                  </IconButton>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function ClientDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [projects, setProjects] = useState([]);
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [error, setError] = useState('');
  const { user, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const {
    isMobile,
    isSmallMobile,
    mobileDrawerOpen,
    toggleMobileDrawer,
    closeMobileDrawer,
    getDrawerWidth,
    getAppBarHeight,
    getContainerPadding
  } = useResponsive();
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCorridor, setSelectedCorridor] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [openFieldOfficerDialog, setOpenFieldOfficerDialog] = useState(false);
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [fieldOfficerAssignments, setFieldOfficerAssignments] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [openFieldOfficersDialog, setOpenFieldOfficersDialog] = useState(false);
  const [openProjectsListDialog, setOpenProjectsListDialog] = useState(false);
  const [showAddFieldOfficer, setShowAddFieldOfficer] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState(null);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);
  const [openFieldOfficerDetails, setOpenFieldOfficerDetails] = useState(false);
  const [openProjectDetails, setOpenProjectDetails] = useState(false);
  const [editingFieldOfficer, setEditingFieldOfficer] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  // Refs for buttons to handle focus/blur on escape
  const fieldOfficersButtonRef = useRef(null);
  const projectsButtonRef = useRef(null);

  // Project form state
  const [projectForm, setProjectForm] = useState({
    po_number: '',
    po_date: '',
    po_party_name: '',
    end_date: '',
    stage_deadlines: {
      site_visit: '',
      design_approval: '',
      product_delivery: '',
      installation: '',
      testing: '',
      commissioning: ''
    },
    document_requirements: {
      site_visit: {
        site_visit_report: true,
        photos_with_geolocation: true
      },
      design_approval: {
        approved_designs_pdf: true,
        photos: true
      },
      product_delivery: {
        eway_bill: true,
        delivery_photos: true,
        proforma_invoice: true,
        acknowledged_grn: true
      },
      installation: {
        installation_photos: true,
        handover_report: true
      }
    },
    line_items: [{
      road_type: '',
      highway_number: '',
      state_code: '',
      corridor_id: '',
      corridor: '',
      stretch_km: '',
      from_lat: '',
      from_lng: '',
      to_lat: '',
      to_lng: '',
      scope_of_work_1: '',
      quantity_1: '',
      scope_of_work_2: '',
      quantity_2: '',
      scope_of_work_3: '',
      quantity_3: ''
    }]
  });

  const [projectFormErrors, setProjectFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add this state after other useState declarations
  const [fieldOfficerForm, setFieldOfficerForm] = useState({
    name: '',
    email: '',
    mobile_number: '',
    country_code: '+91'
  });

  const [fieldOfficerFormErrors, setFieldOfficerFormErrors] = useState({});

  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    officerId: null,
    newPassword: '',
    confirmPassword: '',
    errors: {}
  });

  const [viewOfficerDialog, setViewOfficerDialog] = useState({
    open: false,
    officer: null
  });



  useEffect(() => {
    if (!user) {
      navigate('/client/login');
      return;
    }
    fetchProjects();
    fetchFieldOfficers();
  }, [user, navigate]);

  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/client/projects/${user.client_id}`);
      setProjects(response.data);
    } catch (error) {
      setError('Failed to fetch projects');
    }
  };

  const fetchFieldOfficers = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/client/field-officers/${user.client_id}?limit=5`);
      setFieldOfficers(response.data);
    } catch (error) {
      setError('Failed to fetch field officers');
    }
  };

  const fetchFieldOfficerAssignment = async (corridorId, projectId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${BACKEND_BASE_URL}/api/client/field-officer-assignments/${projectId}/${corridorId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      setFieldOfficerAssignments(prev => ({
        ...prev,
        [corridorId]: data
      }));
    } catch (error) {
      console.error('Error fetching field officer assignment:', error);
    }
  };



  useEffect(() => {
    if (selectedProject) {
      selectedProject.line_items.forEach(corridor => {
        fetchFieldOfficerAssignment(corridor.corridor_id, selectedProject._id);
      });
    }
  }, [selectedProject]);

  // Add escape key listener for dialogs
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (openFieldOfficersDialog) {
          setOpenFieldOfficersDialog(false);
          setShowAddFieldOfficer(false);
          // Blur the field officers button to reset its color
          if (fieldOfficersButtonRef.current) {
            fieldOfficersButtonRef.current.blur();
          }
        }
        if (openProjectsListDialog) {
          setOpenProjectsListDialog(false);
          setShowAddProject(false);
          // Blur the projects button to reset its color
          if (projectsButtonRef.current) {
            projectsButtonRef.current.blur();
          }
        }
        // Also blur any currently focused button to reset colors
        if (document.activeElement && document.activeElement.tagName === 'BUTTON') {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [openFieldOfficersDialog, openProjectsListDialog]);

  const fetchProjectsList = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/client/projects/${user.client_id}?limit=5`);
      setProjectsList(response.data);
    } catch (error) {
      setError('Failed to fetch projects');
    }
  };

  const validateProjectForm = () => {
    let errors = {};
    let isValid = true;

    if (!projectForm.po_number.trim()) {
      errors.po_number = 'PO Number is required';
      isValid = false;
    }

    // Check for duplicate corridor_ids within line items
    const corridorIds = projectForm.line_items.map(item => item.corridor_id);
    const uniqueCorridorIds = new Set(corridorIds);
    if (corridorIds.length !== uniqueCorridorIds.size) {
      projectForm.line_items.forEach((item, index) => {
        if (corridorIds.indexOf(item.corridor_id) !== corridorIds.lastIndexOf(item.corridor_id)) {
          errors[`line_items.${index}.corridor_id`] = 'Corridor ID must be unique';
        }
      });
      isValid = false;
    }

    // Rest of your existing validation...
    if (!projectForm.po_date) {
      errors.po_date = 'PO Date is required';
      isValid = false;
    }
    if (!projectForm.completion_days) {
      errors.completion_days = 'Project Completion Days is required';
      isValid = false;
    } else if (isNaN(projectForm.completion_days) || projectForm.completion_days <= 0) {
      errors.completion_days = 'Project Completion Days must be a positive number';
      isValid = false;
    }

    // Validate line items
    projectForm.line_items.forEach((item, index) => {
      if (!item.corridor_id.trim()) {
        errors[`line_items.${index}.corridor_id`] = 'Corridor ID is required';
        isValid = false;
      }
      if (!item.corridor.trim()) {
        errors[`line_items.${index}.corridor`] = 'Corridor is required';
        isValid = false;
      }
      if (!item.stretch_km) {
        errors[`line_items.${index}.stretch_km`] = 'Stretch (KM) is required';
        isValid = false;
      } else if (isNaN(item.stretch_km) || item.stretch_km <= 0) {
        errors[`line_items.${index}.stretch_km`] = 'Stretch (KM) must be a positive number';
        isValid = false;
      }
      if (!item.from_lat) {
        errors[`line_items.${index}.from_lat`] = 'From Latitude is required';
        isValid = false;
      } else if (isNaN(item.from_lat)) {
        errors[`line_items.${index}.from_lat`] = 'From Latitude must be a number';
        isValid = false;
      }
      if (!item.from_lng) {
        errors[`line_items.${index}.from_lng`] = 'From Longitude is required';
        isValid = false;
      } else if (isNaN(item.from_lng)) {
        errors[`line_items.${index}.from_lng`] = 'From Longitude must be a number';
        isValid = false;
      }
      if (!item.to_lat) {
        errors[`line_items.${index}.to_lat`] = 'To Latitude is required';
        isValid = false;
      } else if (isNaN(item.to_lat)) {
        errors[`line_items.${index}.to_lat`] = 'To Latitude must be a number';
        isValid = false;
      }
      if (!item.to_lng) {
        errors[`line_items.${index}.to_lng`] = 'To Longitude is required';
        isValid = false;
      } else if (isNaN(item.to_lng)) {
        errors[`line_items.${index}.to_lng`] = 'To Longitude must be a number';
        isValid = false;
      }

      // Validate scope of work fields
      if (item.scope_of_work_1 && !item.quantity_1) {
        errors[`line_items.${index}.quantity_1`] = 'Quantity is required when scope of work is provided';
        isValid = false;
      }
      if (item.scope_of_work_2 && !item.quantity_2) {
        errors[`line_items.${index}.quantity_2`] = 'Quantity is required when scope of work is provided';
        isValid = false;
      }
      if (item.scope_of_work_3 && !item.quantity_3) {
        errors[`line_items.${index}.quantity_3`] = 'Quantity is required when scope of work is provided';
        isValid = false;
      }
    });

    // Validate stage deadlines
    const endDate = projectForm.po_date ? new Date(projectForm.po_date) : null;
    if (endDate && projectForm.completion_days) {
      endDate.setDate(endDate.getDate() + parseInt(projectForm.completion_days));
    }
    
    const validateDeadline = (deadline, fieldName) => {
      if (deadline && endDate) {
        const deadlineDate = new Date(deadline);
        if (deadlineDate > endDate) {
          errors[fieldName] = 'Deadline cannot be beyond project completion date';
          isValid = false;
        }
      }
    };

    validateDeadline(projectForm.site_visit_deadline, 'site_visit_deadline');
    validateDeadline(projectForm.design_approval_deadline, 'design_approval_deadline');
    validateDeadline(projectForm.product_delivery_deadline, 'product_delivery_deadline');
    validateDeadline(projectForm.installation_deadline, 'installation_deadline');

    setProjectFormErrors(errors);
    return isValid;
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    if (!validateProjectForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert logo to base64 if present
      let logoBase64 = null;
      if (projectForm.logo) {
        const reader = new FileReader();
        logoBase64 = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(projectForm.logo);
        });
      }

      // Structure stage deadlines properly for backend
      const stageDeadlines = {
        site_visit: projectForm.site_visit_deadline || '',
        design_approval: projectForm.design_approval_deadline || '',
        product_delivery: projectForm.product_delivery_deadline || '',
        installation: projectForm.installation_deadline || ''
      };

      const projectData = {
        ...projectForm,
        logo: logoBase64,
        client_id: user.client_id,
        stage_deadlines: stageDeadlines
      };

      // Remove individual deadline fields as they're now in stage_deadlines
      delete projectData.site_visit_deadline;
      delete projectData.design_approval_deadline;
      delete projectData.product_delivery_deadline;
      delete projectData.installation_deadline;

      if (projectForm._id) {
        await axios.put(`${BACKEND_BASE_URL}/api/client/projects/${projectForm._id}`, projectData);
      } else {
        await axios.post(`${BACKEND_BASE_URL}/api/client/projects`, projectData);
      }
      setOpenProjectDialog(false);
      resetProjectForm();
      fetchProjects();
      setSnackbar({
        open: true,
        message: projectForm._id ? 'Project updated successfully!' : 'Project created successfully!',
        severity: 'success'
      });
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setProjectFormErrors({
          po_number: 'This PO number is already registered'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to create or update project',
          severity: 'error'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => {
    // Save form data to localStorage
    localStorage.setItem('projectDraft', JSON.stringify(projectForm));
    setError('Draft saved successfully');
    // Clear the success message after 3 seconds
    setTimeout(() => {
      setError('');
    }, 3000);
  };

  const handleLoadDraft = () => {
    const draft = localStorage.getItem('projectDraft');
    if (draft) {
      const parsedDraft = JSON.parse(draft);

      // Ensure document_requirements exists with default values if not present
      const defaultDocumentRequirements = {
        site_visit: {
          site_visit_report: true,
          photos_with_geolocation: true
        },
        design_approval: {
          approved_designs_pdf: true,
          photos: true
        },
        product_delivery: {
          eway_bill: true,
          delivery_photos: true,
          proforma_invoice: true,
          acknowledged_grn: true
        },
        installation: {
          installation_photos: true,
          handover_report: true
        }
      };

      setProjectForm({
        ...parsedDraft,
        document_requirements: parsedDraft.document_requirements || defaultDocumentRequirements
      });
      setError('Draft loaded successfully');
      // Clear the success message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      po_number: '',
      po_date: '',
      po_party_name: '',
      completion_days: '',
      end_date: '',
      stage_deadlines: {
        site_visit: '',
        design_approval: '',
        product_delivery: '',
        installation: '',
        testing: '',
        commissioning: ''
      },
      document_requirements: {
        site_visit: {
          site_visit_report: true,
          photos_with_geolocation: true
        },
        design_approval: {
          approved_designs_pdf: true,
          photos: true
        },
        product_delivery: {
          eway_bill: true,
          delivery_photos: true,
          proforma_invoice: true,
          acknowledged_grn: true
        },
        installation: {
          installation_photos: true,
          handover_report: true
        }
      },
      line_items: [{
        road_type: '',
        highway_number: '',
        state_code: '',
        corridor_id: '',
        corridor: '',
        stretch_km: '',
        from_lat: '',
        from_lng: '',
        to_lat: '',
        to_lng: '',
        scope_of_work_1: '',
        quantity_1: '',
        scope_of_work_2: '',
        quantity_2: '',
        scope_of_work_3: '',
        quantity_3: ''
      }]
    });
    setProjectFormErrors({});
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProjectListClick = (project) => {
    setSelectedProjectDetails(project);
    setOpenProjectDetails(true);
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setSelectedCorridor(null);
    setTabValue(0);
  };

  const handleCorridorClick = (corridor, parentProject) => {
    // Use the passed parent project to maintain proper project context
    if (parentProject) {
      setSelectedProject(parentProject);
    }

    setSelectedCorridor(corridor);
    setTabValue(1);
  };

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Handler for saving field officer from FieldOfficerDialog
  const handleFieldOfficerSave = async (formData) => {
    try {
      if (editingFieldOfficer) {
        // Update existing field officer
        await axios.put(`${BACKEND_BASE_URL}/api/client/field-officers/${editingFieldOfficer._id}`, {
          ...formData,
          client_id: user.client_id
        });
        setSnackbar({
          open: true,
          message: 'Field officer updated successfully!',
          severity: 'success'
        });
      } else {
        // Create new field officer
        await axios.post(`${BACKEND_BASE_URL}/api/client/field-officers`, {
          ...formData,
          client_id: user.client_id
        });
        setSnackbar({
          open: true,
          message: 'Field officer added successfully!',
          severity: 'success'
        });
      }

      // Refresh the field officers list
      fetchFieldOfficers();

    } catch (error) {
      console.error('Error saving field officer:', error);
      // Always re-throw the error so FieldOfficerDialog can handle it properly
      // The FieldOfficerDialog will show field-specific errors or general errors as appropriate
      throw error;
    }
  };

  const handleFieldOfficerClick = (officer) => {
    setSelectedFieldOfficer(officer);
    setOpenFieldOfficerDetails(true);
  };

  // Update the handleEditFieldOfficer function
  const handleEditFieldOfficer = (officer) => {
    setEditingFieldOfficer(officer);
    setOpenFieldOfficersDialog(false);
    setOpenFieldOfficerDialog(true);
  };

  const handleDeleteFieldOfficer = async (officerId) => {
    if (window.confirm('Are you sure you want to delete this field officer?')) {
      try {
        await axios.delete(`${BACKEND_BASE_URL}/api/client/field-officers/${officerId}`);
        fetchFieldOfficers();
        setSnackbar({
          open: true,
          message: 'Field officer deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to delete field officer',
          severity: 'error'
        });
      }
    }
  };

  const handleEditProject = (project) => {
    // Format dates for the form
    const formatDate = (date) => {
      if (!date) return '';
      try {
        return new Date(date).toISOString().split('T')[0];
      } catch (e) {
        return '';
      }
    };

    // Ensure document_requirements exists with default values if not present
    const defaultDocumentRequirements = {
      site_visit: {
        site_visit_report: true,
        photos_with_geolocation: true
      },
      design_approval: {
        approved_designs_pdf: true,
        photos: true
      },
      product_delivery: {
        eway_bill: true,
        delivery_photos: true,
        proforma_invoice: true,
        acknowledged_grn: true
      },
      installation: {
        installation_photos: true,
        handover_report: true
      }
    };

    setProjectForm({
      ...project,
      po_date: formatDate(project.po_date),
      site_visit_deadline: formatDate(project.stage_deadlines?.site_visit),
      design_approval_deadline: formatDate(project.stage_deadlines?.design_approval),
      product_delivery_deadline: formatDate(project.stage_deadlines?.product_delivery),
      installation_deadline: formatDate(project.stage_deadlines?.installation),
      document_requirements: project.document_requirements || defaultDocumentRequirements
    });
    setOpenProjectsListDialog(false);
    setOpenProjectDialog(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.delete(`${BACKEND_BASE_URL}/api/client/projects/${projectId}`);
        fetchProjectsList();
        setError('Project deleted successfully');
        setTimeout(() => setError(''), 3000);
      } catch (error) {
        setError('Failed to delete project');
      }
    }
  };

  // Add the generateCorridorId function
  const generateCorridorId = (roadType, highwayNumber, stateCode) => {
    if (roadType && highwayNumber && stateCode) {
      return `${roadType}-${highwayNumber}-${stateCode}`;
    }
    return '';
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        // Process the data and create line items
        const newLineItems = jsonData.map(row => {
          const [from_lat, from_lng] = (row.from_coord || '').split(',').map(coord => coord.trim());
          const [to_lat, to_lng] = (row.to_coord || '').split(',').map(coord => coord.trim());
          
          return {
            road_type: '',
            highway_number: '',
            state_code: '',
            corridor_id: '', // This will be generated when road_type, highway_number, and state_code are set
            corridor: `${row.from_loc} to ${row.to_loc}`,
            stretch_km: row.kms || '',
            from_lat: from_lat || '',
            from_lng: from_lng || '',
            to_lat: to_lat || '',
            to_lng: to_lng || '',
            scope_of_work_1: row.scope_of_work_1 || '',
            quantity_1: row.quantity_1 || '',
            scope_of_work_2: row.scope_of_work_2 || '',
            quantity_2: row.quantity_2 || '',
            scope_of_work_3: row.scope_of_work_3 || '',
            quantity_3: row.quantity_3 || ''
          };
        });

        setProjectForm(prev => ({
          ...prev,
          line_items: newLineItems
        }));
      } catch (error) {
        console.error('Error processing file:', error);
        setError('Error processing file. Please make sure it\'s a valid Excel/CSV file with the required columns.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleUpdatePassword = async () => {
    // Validate passwords
    const errors = {};
    if (!passwordDialog.newPassword) {
      errors.newPassword = 'Password is required';
    }
    if (!passwordDialog.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    }
    if (passwordDialog.newPassword !== passwordDialog.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordDialog(prev => ({ ...prev, errors }));
      return;
    }

    try {
      await axios.put(`${BACKEND_BASE_URL}/api/client/field-officers/${passwordDialog.officerId}/password`, {
        password: passwordDialog.newPassword
      });
      setPasswordDialog({ 
        open: false, 
        officerId: null, 
        newPassword: '', 
        confirmPassword: '',
        errors: {}
      });
      setSnackbar({
        open: true,
        message: 'Password updated successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update password',
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#F79233',
          height: getAppBarHeight()
        }}
      >
        <Toolbar sx={{ minHeight: getAppBarHeight() }}>
          {/* Hamburger menu for mobile */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleMobileDrawer}
              sx={{ mr: 2 }}
              className="hamburger-menu"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant={isSmallMobile ? "subtitle1" : "h6"}
            sx={{
              flexGrow: 1,
              fontSize: isSmallMobile ? '1rem' : '1.25rem'
            }}
            className="responsive-text"
          >
            {user?.organization_name || 'Client Dashboard'}
          </Typography>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: isSmallMobile ? 1 : 2,
            flexWrap: 'nowrap' // Ensure buttons stay horizontal on mobile
          }}>
            <Button
              ref={fieldOfficersButtonRef}
              variant="contained"
              startIcon={!isSmallMobile && <PersonIcon />}
              onClick={() => {
                setOpenFieldOfficersDialog(true);
                setShowAddFieldOfficer(false);
                fetchFieldOfficers();
              }}
              sx={{
                backgroundColor: '#F79233',
                '&:hover': { backgroundColor: '#E67E22' },
                '&:focus': { backgroundColor: '#F79233' }, // Ensure focus state matches default
                fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                padding: isSmallMobile ? '4px 8px' : '6px 16px',
                minWidth: isSmallMobile ? 'auto' : '64px'
              }}
              size={isSmallMobile ? "small" : "medium"}
            >
              {isSmallMobile ? 'Officers' : 'Field Officers'}
            </Button>
            <Button
              ref={projectsButtonRef}
              variant="contained"
              startIcon={!isSmallMobile && <AssignmentIcon />}
              onClick={() => {
                setOpenProjectsListDialog(true);
                setShowAddProject(false);
                fetchProjectsList();
              }}
              sx={{
                backgroundColor: '#F79233',
                '&:hover': { backgroundColor: '#E67E22' },
                '&:focus': { backgroundColor: '#F79233' }, // Ensure focus state matches default
                fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                padding: isSmallMobile ? '4px 8px' : '6px 16px',
                minWidth: isSmallMobile ? 'auto' : '64px'
              }}
              size={isSmallMobile ? "small" : "medium"}
            >
              Projects
            </Button>
          </Box>

          <IconButton
            color="inherit"
            onClick={handleLogout}
            sx={{
              ml: isSmallMobile ? 1 : 2,
              padding: isSmallMobile ? '4px' : '8px'
            }}
            size={isSmallMobile ? "small" : "medium"}
          >
            <LogoutIcon fontSize={isSmallMobile ? "small" : "medium"} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: 300,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 300,
              boxSizing: 'border-box',
              marginTop: `${getAppBarHeight()}px`,
              height: `calc(100vh - ${getAppBarHeight()}px)`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            },
          }}
        >
          <List sx={{
            padding: 0,
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}>
            {projects.map((project) => (
              <React.Fragment key={project._id}>
                <ListItemButton
                  onClick={() => handleProjectClick(project)}
                  selected={selectedProjectDetails?._id === project._id}
                  sx={{
                    minHeight: 48,
                    px: 2,
                    py: 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <AssignmentIcon sx={{ color: '#F79233' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={project.po_number}
                    secondary={`Corridors: ${project.line_items.length}`}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem'
                    }}
                  />
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProjectExpand(project._id);
                    }}
                    size="small"
                  >
                    {expandedProjects[project._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </ListItemButton>
                <Collapse in={expandedProjects[project._id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {project.line_items.map((corridor, index) => (
                      <ListItemButton
                        key={index}
                        sx={{
                          pl: 4,
                          minHeight: 40,
                          py: 0.5
                        }}
                        onClick={() => handleCorridorClick(corridor, project)}
                        selected={selectedCorridor === corridor}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <RouteIcon sx={{ color: '#F79233', fontSize: '1rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={corridor.corridor_id}
                          primaryTypographyProps={{
                            fontSize: '0.8rem'
                          }}
                          className="responsive-text"
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={closeMobileDrawer}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: getDrawerWidth(),
              boxSizing: 'border-box',
              marginTop: `${getAppBarHeight()}px`,
              height: `calc(100vh - ${getAppBarHeight()}px)`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            },
          }}
        >
          <List sx={{
            padding: 0,
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}>
            {projects.map((project) => (
              <React.Fragment key={project._id}>
                <ListItemButton
                  onClick={() => {
                    handleProjectClick(project);
                    if (isSmallMobile) closeMobileDrawer();
                  }}
                  selected={selectedProjectDetails?._id === project._id}
                  sx={{
                    minHeight: 48,
                    px: 2,
                    py: 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <AssignmentIcon sx={{ color: '#F79233' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={project.po_number}
                    secondary={`Corridors: ${project.line_items.length}`}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem'
                    }}
                    className="responsive-text"
                  />
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProjectExpand(project._id);
                    }}
                    size="small"
                  >
                    {expandedProjects[project._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </ListItemButton>
                <Collapse in={expandedProjects[project._id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {project.line_items.map((corridor, index) => (
                      <ListItemButton
                        key={index}
                        sx={{
                          pl: 4,
                          minHeight: 40,
                          py: 0.5
                        }}
                        onClick={() => {
                          handleCorridorClick(corridor, project);
                          if (isSmallMobile) closeMobileDrawer();
                        }}
                        selected={selectedCorridor === corridor}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <RouteIcon sx={{ color: '#F79233', fontSize: '1rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={corridor.corridor_id}
                          primaryTypographyProps={{
                            fontSize: '0.8rem'
                          }}
                          className="responsive-text"
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: isMobile ? '100%' : `calc(100% - 300px)`,
          marginTop: `${getAppBarHeight()}px`,
          backgroundColor: '#f5f5f5',
          minHeight: `calc(100vh - ${getAppBarHeight()}px)`,
          overflow: 'auto'
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              mx: getContainerPadding(),
              mt: getContainerPadding(),
              fontSize: isSmallMobile ? '0.875rem' : '1rem'
            }}
          >
            {error}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          {selectedProject && <ProjectDashboard project={selectedProject} />}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {selectedCorridor && <CorridorDashboard corridor={selectedCorridor} fieldOfficers={fieldOfficers} project={selectedProject} />}
        </TabPanel>

        {!selectedProject && !selectedCorridor && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            mt: 4,
            px: getContainerPadding()
          }}>
            <Typography
              variant={isSmallMobile ? "body1" : "h6"}
              color="text.secondary"
              sx={{ textAlign: 'center' }}
              className="responsive-text"
            >
              Select a project to view details
            </Typography>
          </Box>
        )}
      </Box>

      {/* Add/Edit Project Dialog */}
      <Dialog
        open={openProjectDialog}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          setOpenProjectDialog(false);
          resetProjectForm();
          setProjectFormErrors({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {projectForm._id ? 'Update Project' : 'Add New Project'}
            </Typography>
            {!projectForm._id && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Tooltip title="Save Draft">
                  <IconButton onClick={handleSaveDraft} size="small">
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Load Draft">
                  <IconButton 
                    onClick={handleLoadDraft} 
                    size="small"
                    disabled={!localStorage.getItem('projectDraft')}
                  >
                    <RestoreIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear Form">
                  <IconButton onClick={resetProjectForm} size="small">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleProjectSubmit}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PO Number"
                  value={projectForm.po_number}
                  onChange={(e) => setProjectForm({ ...projectForm, po_number: e.target.value })}
                  required
                  error={!!projectFormErrors.po_number}
                  helperText={projectFormErrors.po_number}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="PO Date"
                  value={projectForm.po_date}
                  onChange={(e) => {
                    setProjectForm({
                      ...projectForm,
                      po_date: e.target.value
                    });
                  }}
                  error={!!projectFormErrors.po_date}
                  helperText={projectFormErrors.po_date}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PO Party - Full Legal Entity Name"
                  value={projectForm.po_party_name}
                  onChange={(e) => setProjectForm({ ...projectForm, po_party_name: e.target.value })}
                  required
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="file"
                  label="Logo of the PO Party (Optional)"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: 'image/jpeg,image/jpg' }}
                  onChange={(e) => setProjectForm({ ...projectForm, logo: e.target.files[0] })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Project Completion Days"
                  value={projectForm.completion_days}
                  onChange={(e) => setProjectForm({ ...projectForm, completion_days: e.target.value })}
                  required
                  error={!!projectFormErrors.completion_days}
                  helperText={projectFormErrors.completion_days}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Site Visit Deadline"
                  value={projectForm.site_visit_deadline ? new Date(projectForm.site_visit_deadline).toISOString().split('T')[0] : ''}
                  onChange={(e) => setProjectForm({ ...projectForm, site_visit_deadline: e.target.value })}
                  error={!!projectFormErrors.site_visit_deadline}
                  helperText={projectFormErrors.site_visit_deadline}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Design Approval Deadline"
                  value={projectForm.design_approval_deadline ? new Date(projectForm.design_approval_deadline).toISOString().split('T')[0] : ''}
                  onChange={(e) => setProjectForm({ ...projectForm, design_approval_deadline: e.target.value })}
                  error={!!projectFormErrors.design_approval_deadline}
                  helperText={projectFormErrors.design_approval_deadline}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Material Delivery Deadline"
                  value={projectForm.product_delivery_deadline ? new Date(projectForm.product_delivery_deadline).toISOString().split('T')[0] : ''}
                  onChange={(e) => setProjectForm({ ...projectForm, product_delivery_deadline: e.target.value })}
                  error={!!projectFormErrors.product_delivery_deadline}
                  helperText={projectFormErrors.product_delivery_deadline}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Installation Deadline"
                  value={projectForm.installation_deadline ? new Date(projectForm.installation_deadline).toISOString().split('T')[0] : ''}
                  onChange={(e) => setProjectForm({ ...projectForm, installation_deadline: e.target.value })}
                  error={!!projectFormErrors.installation_deadline}
                  helperText={projectFormErrors.installation_deadline}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#F79233' }}>
                  Document Requirements Configuration
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Configure which documents are required for each project stage. By default, all documents are mandatory.
                </Typography>

                {/* Site Visit Documents */}
                <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: '#F79233' }}>Site Visit</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.site_visit?.site_visit_report || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                site_visit: {
                                  ...projectForm.document_requirements?.site_visit,
                                  site_visit_report: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Site Visit Report"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.site_visit?.photos_with_geolocation || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                site_visit: {
                                  ...projectForm.document_requirements?.site_visit,
                                  photos_with_geolocation: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Photos with Geo-location & Timestamp"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Design Approval Documents */}
                <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: '#F79233' }}>Design Approval</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.design_approval?.approved_designs_pdf || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                design_approval: {
                                  ...projectForm.document_requirements?.design_approval,
                                  approved_designs_pdf: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Copy of Approved Designs (PDF)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.design_approval?.photos || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                design_approval: {
                                  ...projectForm.document_requirements?.design_approval,
                                  photos: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Multiple Photos"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Product Delivery Documents */}
                <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: '#F79233' }}>Product Delivery</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.product_delivery?.eway_bill || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                product_delivery: {
                                  ...projectForm.document_requirements?.product_delivery,
                                  eway_bill: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Copy of E-way Bill"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.product_delivery?.delivery_photos || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                product_delivery: {
                                  ...projectForm.document_requirements?.product_delivery,
                                  delivery_photos: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Photo Evidence of Delivery"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.product_delivery?.proforma_invoice || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                product_delivery: {
                                  ...projectForm.document_requirements?.product_delivery,
                                  proforma_invoice: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Proforma Invoice"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.product_delivery?.acknowledged_grn || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                product_delivery: {
                                  ...projectForm.document_requirements?.product_delivery,
                                  acknowledged_grn: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Acknowledged GRN (Goods Receipt Note)"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Installation Documents */}
                <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: '#F79233' }}>Installation</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.installation?.installation_photos || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                installation: {
                                  ...projectForm.document_requirements?.installation,
                                  installation_photos: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Photo Evidence of Installation"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={projectForm.document_requirements?.installation?.handover_report || false}
                            onChange={(e) => setProjectForm({
                              ...projectForm,
                              document_requirements: {
                                ...projectForm.document_requirements,
                                installation: {
                                  ...projectForm.document_requirements?.installation,
                                  handover_report: e.target.checked
                                }
                              }
                            })}
                          />
                        }
                        label="Handover Report"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#F79233' }}>
                  Upload Corridor Data
                </Typography>
                <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Upload an Excel/CSV file with the following columns:
                    <br />
                    from_loc, to_loc, from_coord, to_coord, kms, scope_of_work_1, quantity_1, scope_of_work_2, quantity_2, scope_of_work_3, quantity_3
                  </Typography>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    id="corridor-file-upload"
                  />
                  <label htmlFor="corridor-file-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<AddIcon />}
                      sx={{ 
                        backgroundColor: '#F79233', 
                        '&:hover': { backgroundColor: '#E67E22' },
                        mb: 2
                      }}
                    >
                      Upload File
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                    Supported formats: .xlsx, .xls, .csv
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                  Line Items
                </Typography>
                {projectForm.line_items.map((item, index) => (
                  <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Line Item {index + 1}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth margin="dense">
                          <InputLabel>Road Type</InputLabel>
                          <Select
                            value={item.road_type || ''}
                            onChange={(e) => {
                              const newItems = [...projectForm.line_items];
                              newItems[index] = {
                                ...newItems[index],
                                road_type: e.target.value,
                                corridor_id: generateCorridorId(
                                  e.target.value,
                                  newItems[index].highway_number,
                                  newItems[index].state_code
                                )
                              };
                              setProjectForm({ ...projectForm, line_items: newItems });
                            }}
                            label="Road Type"
                          >
                            <MenuItem value="NH">National Highway</MenuItem>
                            <MenuItem value="SH">State Highway</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Highway Number"
                          type="text"
                          value={item.highway_number || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newItems = [...projectForm.line_items];
                            newItems[index] = {
                              ...newItems[index],
                              highway_number: value,
                              corridor_id: generateCorridorId(
                                newItems[index].road_type,
                                value,
                                newItems[index].state_code
                              )
                            };
                            setProjectForm({ ...projectForm, line_items: newItems });
                          }}
                          inputProps={{ min: 1, max: 50 }}
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth margin="dense">
                          <InputLabel>State</InputLabel>
                          <Select
                            value={item.state_code || ''}
                            onChange={(e) => {
                              const newItems = [...projectForm.line_items];
                              newItems[index] = {
                                ...newItems[index],
                                state_code: e.target.value,
                                corridor_id: generateCorridorId(
                                  newItems[index].road_type,
                                  newItems[index].highway_number,
                                  e.target.value
                                )
                              };
                              setProjectForm({ ...projectForm, line_items: newItems });
                            }}
                            label="State"
                          >
                            {INDIAN_STATES.map((state) => (
                              <MenuItem key={state.code} value={state.code}>
                                {state.code} - {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Corridor ID"
                          value={item.corridor_id || ''}
                          InputProps={{
                            readOnly: true,
                          }}
                          required
                          error={!!projectFormErrors[`line_items.${index}.corridor_id`]}
                          helperText={projectFormErrors[`line_items.${index}.corridor_id`]}
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Corridor"
                          value={item.corridor}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].corridor = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          required
                          error={!!projectFormErrors[`line_items.${index}.corridor`]}
                          helperText={projectFormErrors[`line_items.${index}.corridor`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Stretch (KM)"
                          value={item.stretch_km}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].stretch_km = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          required
                          error={!!projectFormErrors[`line_items.${index}.stretch_km`]}
                          helperText={projectFormErrors[`line_items.${index}.stretch_km`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="From Latitude"
                          value={item.from_lat}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].from_lat = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          required
                          error={!!projectFormErrors[`line_items.${index}.from_lat`]}
                          helperText={projectFormErrors[`line_items.${index}.from_lat`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="From Longitude"
                          value={item.from_lng}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].from_lng = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          required
                          error={!!projectFormErrors[`line_items.${index}.from_lng`]}
                          helperText={projectFormErrors[`line_items.${index}.from_lng`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="To Latitude"
                          value={item.to_lat}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].to_lat = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          required
                          error={!!projectFormErrors[`line_items.${index}.to_lat`]}
                          helperText={projectFormErrors[`line_items.${index}.to_lat`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="To Longitude"
                          value={item.to_lng}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].to_lng = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          required
                          error={!!projectFormErrors[`line_items.${index}.to_lng`]}
                          helperText={projectFormErrors[`line_items.${index}.to_lng`]}
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Scope of Work</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Scope of Work 1"
                          value={item.scope_of_work_1}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].scope_of_work_1 = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          error={!!projectFormErrors[`line_items.${index}.scope_of_work_1`]}
                          helperText={projectFormErrors[`line_items.${index}.scope_of_work_1`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Quantity 1"
                          value={item.quantity_1}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].quantity_1 = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          error={!!projectFormErrors[`line_items.${index}.quantity_1`]}
                          helperText={projectFormErrors[`line_items.${index}.quantity_1`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Scope of Work 2"
                          value={item.scope_of_work_2}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].scope_of_work_2 = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          error={!!projectFormErrors[`line_items.${index}.scope_of_work_2`]}
                          helperText={projectFormErrors[`line_items.${index}.scope_of_work_2`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Quantity 2"
                          value={item.quantity_2}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].quantity_2 = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          error={!!projectFormErrors[`line_items.${index}.quantity_2`]}
                          helperText={projectFormErrors[`line_items.${index}.quantity_2`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Scope of Work 3"
                          value={item.scope_of_work_3}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].scope_of_work_3 = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          error={!!projectFormErrors[`line_items.${index}.scope_of_work_3`]}
                          helperText={projectFormErrors[`line_items.${index}.scope_of_work_3`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Quantity 3"
                          value={item.quantity_3}
                          onChange={(e) => {
                            const newLineItems = [...projectForm.line_items];
                            newLineItems[index].quantity_3 = e.target.value;
                            setProjectForm({ ...projectForm, line_items: newLineItems });
                          }}
                          error={!!projectFormErrors[`line_items.${index}.quantity_3`]}
                          helperText={projectFormErrors[`line_items.${index}.quantity_3`]}
                        />
                      </Grid>
                    </Grid>
                    {index > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          const newLineItems = projectForm.line_items.filter((_, i) => i !== index);
                          setProjectForm({ ...projectForm, line_items: newLineItems });
                        }}
                        sx={{ mt: 2 }}
                      >
                        Remove Line Item
                      </Button>
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setProjectForm({
                      ...projectForm,
                      line_items: [
                        ...projectForm.line_items,
                        {
                          road_type: '',
                          highway_number: '',
                          state_code: '',
                          corridor_id: '',
                          corridor: '',
                          stretch_km: '',
                          from_lat: '',
                          from_lng: '',
                          to_lat: '',
                          to_lng: '',
                          scope_of_work_1: '',
                          quantity_1: '',
                          scope_of_work_2: '',
                          quantity_2: '',
                          scope_of_work_3: '',
                          quantity_3: ''
                        }
                      ]
                    });
                  }}
                  sx={{ mt: 2 }}
                >
                  Add Line Item
                </Button>
              </Grid>
            </Grid>

            <DialogActions>
              <Button onClick={() => {
                setOpenProjectDialog(false);
                resetProjectForm();
                setProjectFormErrors({});
              }}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {projectForm._id ? 'Update Project' : 'Add Project'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add the FieldOfficerDialog component */}
      <FieldOfficerDialog
        open={openFieldOfficerDialog}
        onClose={() => {
          setOpenFieldOfficerDialog(false);
          setEditingFieldOfficer(null);
        }}
        onSave={handleFieldOfficerSave}
        clientId={user.client_id}
        editData={editingFieldOfficer}
      />

      {/* Update the Field Officers Dialog */}
      <Dialog
        open={openFieldOfficersDialog}
        onClose={() => {
          setOpenFieldOfficersDialog(false);
          setShowAddFieldOfficer(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {showAddFieldOfficer ? 'Add Field Officer' : 'Field Officers'}
            </Typography>
            <Box>
              {!showAddFieldOfficer && (
                <IconButton
                  onClick={() => {
                    setOpenFieldOfficersDialog(false);
                    setOpenFieldOfficerDialog(true);
                  }}
                  sx={{ mr: 1 }}
                >
                  <AddIcon />
                </IconButton>
              )}
              <IconButton onClick={() => {
                setOpenFieldOfficersDialog(false);
                setShowAddFieldOfficer(false);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {fieldOfficers.map((officer) => (
              <ListItem
                key={officer._id}
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: isMobile ? 1 : 0,
                  py: isMobile ? 2 : 1
                }}
              >
                <ListItemText
                  primary={officer.name}
                  secondary={`${officer.email} | ${officer.mobile_number}`}
                  sx={{
                    mb: isMobile ? 1 : 0,
                    minWidth: isMobile ? '100%' : 'auto'
                  }}
                />
                <Box sx={{
                  display: 'flex',
                  gap: isSmallMobile ? 0.5 : 1,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  justifyContent: isMobile ? 'center' : 'flex-end',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <Tooltip title="View Details">
                    <IconButton
                      onClick={() => setViewOfficerDialog({ open: true, officer })}
                      size={isSmallMobile ? "small" : "medium"}
                      className="touch-target"
                    >
                      <VisibilityIcon fontSize={isSmallMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Field Officer">
                    <IconButton
                      onClick={() => handleEditFieldOfficer(officer)}
                      size={isSmallMobile ? "small" : "medium"}
                      className="touch-target"
                    >
                      <EditIcon fontSize={isSmallMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Change Password">
                    <IconButton
                      onClick={() => setPasswordDialog({
                        open: true,
                        officerId: officer._id,
                        newPassword: '',
                        confirmPassword: '',
                        errors: {}
                      })}
                      size={isSmallMobile ? "small" : "medium"}
                      className="touch-target"
                    >
                      <LockIcon fontSize={isSmallMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Field Officer">
                    <IconButton
                      onClick={() => handleDeleteFieldOfficer(officer._id)}
                      size={isSmallMobile ? "small" : "medium"}
                      className="touch-target"
                    >
                      <DeleteIcon fontSize={isSmallMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Update the Projects List Dialog */}
      <Dialog
        open={openProjectsListDialog}
        onClose={() => {
          setOpenProjectsListDialog(false);
          setShowAddProject(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {showAddProject ? 'Add New Project' : 'Projects'}
            </Typography>
            <Box>
              {!showAddProject && (
                <IconButton
                  onClick={() => {
                    setOpenProjectsListDialog(false);
                    setOpenProjectDialog(true);
                  }}
                  sx={{ mr: 1 }}
                >
                  <AddIcon />
                </IconButton>
              )}
              <IconButton onClick={() => {
                setOpenProjectsListDialog(false);
                setShowAddProject(false);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {projectsList.map((project) => (
              <ListItem
                key={project._id}
                button
                onClick={() => handleProjectListClick(project)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(247, 146, 51, 0.1)'
                  },
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: isMobile ? 1 : 0,
                  py: isMobile ? 2 : 1
                }}
              >
                <ListItemText
                  primary={project.po_number}
                  secondary={`${project.po_party_name} | ${new Date(project.po_date).toLocaleDateString()} | ${project.completion_days} days`}
                  sx={{
                    mb: isMobile ? 1 : 0,
                    minWidth: isMobile ? '100%' : 'auto'
                  }}
                />
                <Box sx={{
                  display: 'flex',
                  gap: isSmallMobile ? 0.5 : 1,
                  justifyContent: isMobile ? 'center' : 'flex-end',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <Tooltip title="Edit Project">
                    <IconButton
                      size={isSmallMobile ? "small" : "medium"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProject(project);
                      }}
                      className="touch-target"
                    >
                      <EditIcon fontSize={isSmallMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Project">
                    <IconButton
                      size={isSmallMobile ? "small" : "medium"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(project._id);
                      }}
                      className="touch-target"
                    >
                      <DeleteIcon fontSize={isSmallMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Add Field Officer Details Dialog */}
      <Dialog
        open={openFieldOfficerDetails}
        onClose={() => setOpenFieldOfficerDetails(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Field Officer Details</Typography>
            <IconButton onClick={() => setOpenFieldOfficerDetails(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFieldOfficer && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Name</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedFieldOfficer.name}</Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Email</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedFieldOfficer.email}</Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Mobile Number</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedFieldOfficer.mobile_number}</Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Country Code</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedFieldOfficer.country_code}</Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Created At</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {new Date(selectedFieldOfficer.created_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog
        open={openProjectDetails}
        onClose={() => setOpenProjectDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Project Details
            </Typography>
            <IconButton onClick={() => setOpenProjectDetails(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProjectDetails && (
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#F79233' }}>
                  Project Overview
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>PO Number</TableCell>
                        <TableCell>PO Date</TableCell>
                        <TableCell>PO Party</TableCell>
                        <TableCell>End Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{selectedProjectDetails.po_number || 'Not set'}</TableCell>
                        <TableCell>{formatIndianDate(selectedProjectDetails.po_date) || 'Not set'}</TableCell>
                        <TableCell>{selectedProjectDetails.po_party_name || 'Not set'}</TableCell>
                        <TableCell>{formatIndianDate(selectedProjectDetails.end_date) || 'Not set'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#F79233' }}>
                  Line Items
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Corridor ID</TableCell>
                        <TableCell>Corridor</TableCell>
                        <TableCell>Stretch (KM)</TableCell>
                        <TableCell>From Coordinates</TableCell>
                        <TableCell>To Coordinates</TableCell>
                        <TableCell>Scope of Work</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProjectDetails.line_items && selectedProjectDetails.line_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.corridor_id || 'Not set'}</TableCell>
                          <TableCell>{item.corridor || 'Not set'}</TableCell>
                          <TableCell>{item.stretch_km ? `${item.stretch_km} km` : 'Not set'}</TableCell>
                          <TableCell>
                            {item.from_lat && item.from_lng 
                              ? `Lat: ${item.from_lat}, Lng: ${item.from_lng}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {item.to_lat && item.to_lng 
                              ? `Lat: ${item.to_lat}, Lng: ${item.to_lng}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Box>
                              {item.scope_of_work_1 && (
                                <Typography variant="body2">
                                  {item.scope_of_work_1}: {item.quantity_1}
                                </Typography>
                              )}
                              {item.scope_of_work_2 && (
                                <Typography variant="body2">
                                  {item.scope_of_work_2}: {item.quantity_2}
                                </Typography>
                              )}
                              {item.scope_of_work_3 && (
                                <Typography variant="body2">
                                  {item.scope_of_work_3}: {item.quantity_3}
                                </Typography>
                              )}
                              {!item.scope_of_work_1 && !item.scope_of_work_2 && !item.scope_of_work_3 && (
                                <Typography variant="body2">No scope of work defined</Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Update Dialog */}
      <Dialog open={passwordDialog.open} onClose={() => setPasswordDialog({ 
        open: false, 
        officerId: null, 
        newPassword: '', 
        confirmPassword: '',
        errors: {}
      })}>
        <DialogTitle>Update Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={passwordDialog.newPassword}
            onChange={(e) => setPasswordDialog(prev => ({ 
              ...prev, 
              newPassword: e.target.value,
              errors: { ...prev.errors, newPassword: '' }
            }))}
            error={!!passwordDialog.errors.newPassword}
            helperText={passwordDialog.errors.newPassword}
          />
          <TextField
            margin="dense"
            label="Confirm Password"
            type="password"
            fullWidth
            value={passwordDialog.confirmPassword}
            onChange={(e) => setPasswordDialog(prev => ({ 
              ...prev, 
              confirmPassword: e.target.value,
              errors: { ...prev.errors, confirmPassword: '' }
            }))}
            error={!!passwordDialog.errors.confirmPassword}
            helperText={passwordDialog.errors.confirmPassword}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog({ 
            open: false, 
            officerId: null, 
            newPassword: '', 
            confirmPassword: '',
            errors: {}
          })}>Cancel</Button>
          <Button onClick={handleUpdatePassword} variant="contained" color="primary">
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Field Officer Dialog */}
      <Dialog open={viewOfficerDialog.open} onClose={() => setViewOfficerDialog({ open: false, officer: null })}>
        <DialogTitle>Field Officer Details</DialogTitle>
        <DialogContent>
          {viewOfficerDialog.officer && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Name:</strong> {viewOfficerDialog.officer.name}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Email:</strong> {viewOfficerDialog.officer.email}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Mobile Number:</strong> {viewOfficerDialog.officer.mobile_number}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Country:</strong> {viewOfficerDialog.officer.country}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOfficerDialog({ open: false, officer: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ClientDashboard; 